/**
 * FileSourceManager.ts - FileSourceManager - Manages file sources and access
 * 
 * CHANGE HISTORY:
 * - 251021_2028 [c5f5984] danielbeer: fix: Resolve additional TypeScript syntax errors
 * - 251021_2025 [cc6aae8] danielbeer: fix: Resolve TypeScript migration errors
 * - 251021_0517 [8786ca9] danielbeer: feat: apply authr pattern to all 43 remaining packages
 * - 251021_0509 [c090082] danielbeer: feat: complete remaining TypeScript fixes across all packages
 * - 251021_0452 [97a0397] danielbeer: fix: correct automated type annotations and test scripts
 * - 251021_0447 [ce7b5e6] danielbeer: feat: automate TypeScript and Actr integration for all packages
 * - 251021_0421 [7529d5e] danielbeer: feat: migrate all packages to TypeScript structure
 */
interface FileSourceManagerOptions {
  actr?: any; // Actr instance
  [key: string]: any;
}

export class FileSourceManager {
  private actr: any;
  constructor(options: FileSourceManagerOptions = {}) {
    this.options = {
      enableCache: true,
      cacheExpiry: 3600000, // 1 hour
      maxCacheSize: 100 * 1024 * 1024, // 100MB
      retryAttempts: 3,
      retryDelay: 1000,
      ...options
    };
    
    this.adapters = new Map();
    this.cache = new Map();
    this.accessCache = new Map();
  }
  

  /**
   * Register FileSource actions with Actr
   */
  private registerActions(): void {
    if (!this.actr) return;
    
    // checkAccess action
    this.actr.registerAction('draggr:check-access', async (params) => {
      return await this.checkAccess(params);
    });

    // selectBestSource action
    this.actr.registerAction('draggr:select-best-source', async (params) => {
      return await this.selectBestSource(params);
    });

    // getDownloadUrl action
    this.actr.registerAction('draggr:get-download-url', async (params) => {
      return await this.getDownloadUrl(params);
    });

    // fetchFile action
    this.actr.registerAction('draggr:fetch-file', async (params) => {
      return await this.fetchFile(params);
    });

    // fetchFromSources action
    this.actr.registerAction('draggr:fetch-from-sources', async (params) => {
      return await this.fetchFromSources(params);
    });

    // streamFile action
    this.actr.registerAction('draggr:stream-file', async (params) => {
      return await this.streamFile(params);
    });

    // supportsP2P action
    this.actr.registerAction('draggr:supports-p2-p', async (params) => {
      return await this.supportsP2P(params);
    });
  }

  /**
   * Register a source adapter
   */
  registerAdapter(type: string, adapter): void {
    this.adapters.set(type, adapter);
  }
  
  /**
   * Get adapter for source type
   */
  getAdapter(type): any {
    const adapter = this.adapters.get(type);
    if (!adapter) {
      throw new Error(`No adapter registered for type: ${type}`);
    }
    return adapter;
  }
  
  /**
   * Check access rights for a source
   */
  async checkAccess(source, useCache = true): Promise<any> {
    const cacheKey = this.getSourceKey(source);
    
    // Check cache
    if (useCache && this.accessCache.has(cacheKey)) {
      const cached = this.accessCache.get(cacheKey);
      if (Date.now() - cached.timestamp < this.options.cacheExpiry) {
        return cached.access;
      }
    }
    
    // Get adapter and check access
    const adapter = this.getAdapter(source.type);
    const access = await adapter.checkAccess(source);
    
    // Cache result
    this.accessCache.set(cacheKey, {
      access,
      timestamp: Date.now()
    });
    
    return access;
  }
  
  /**
   * Select best source from multiple sources
   */
  async selectBestSource(sources): Promise<any> {
    if (!Array.isArray(sources) || sources.length === 0) {
      throw new Error('No sources provided');
    }
    
    // Sort by priority
    const sorted = [...sources].sort((a, b) => 
      (a.priority || 999) - (b.priority || 999)
    );
    
    // Try each source in order
    for (const source of sorted) {
      try {
        const access = await this.checkAccess(source);
        
        if (access.canRead) {
          return {
            source,
            access
          };
        }
      } catch (error) {
        console.warn(`Source ${source.type} failed:`, error);
        continue;
      }
    }
    
    throw new Error('No accessible sources found');
  }
  
  /**
   * Get download URL for source
   */
  async getDownloadUrl(source): Promise<any> {
    const adapter = this.getAdapter(source.type);
    return await adapter.getDownloadUrl(source);
  }
  
  /**
   * Fetch file from source
   */
  async fetchFile(source, options = {}): Promise<any> {
    const {
      onProgress,
      useCache = true,
      retryAttempts = this.options.retryAttempts
    } = options;
    
    const cacheKey = this.getSourceKey(source);
    
    // Check cache
    if (useCache && this.cache.has(cacheKey)) {
      const cached = this.cache.get(cacheKey);
      if (Date.now() - cached.timestamp < this.options.cacheExpiry) {
        return cached.blob;
      }
    }
    
    // Fetch with retry
    let lastError;
    for (let attempt = 0; attempt < retryAttempts; attempt++) {
      try {
        const adapter = this.getAdapter(source.type);
        const blob = await adapter.fetchBlob(source, onProgress);
        
        // Cache result
        if (useCache) {
          this.cacheBlob(cacheKey, blob);
        }
        
        return blob;
      } catch (error) {
        lastError = error;
        console.warn(`Fetch attempt ${attempt + 1} failed:`, error);
        
        if (attempt < retryAttempts - 1) {
          await this.delay(this.options.retryDelay * (attempt + 1));
        }
      }
    }
    
    throw lastError;
  }
  
  /**
   * Fetch from multiple sources with fallback
   */
  async fetchFromSources(sources, options = {}): Promise<any> {
    const { source, access } = await this.selectBestSource(sources);
    
    if (!access.canRead) {
      throw new Error('No read access to selected source');
    }
    
    return await this.fetchFile(source, options);
  }
  
  /**
   * Stream file with progress
   */
  async streamFile(source, onProgress): Promise<any> {
    const adapter = this.getAdapter(source.type);
    return await adapter.streamFile(source, onProgress);
  }
  
  /**
   * Check if P2P transfer is available
   */
  async supportsP2P(source): Promise<any> {
    const adapter = this.getAdapter(source.type);
    return await adapter.supportsP2P(source);
  }
  
  /**
   * Cache blob
   */
  cacheBlob(key: string, blob): void {
    // Check cache size
    let totalSize = blob.size;
    for (const [, cached] of this.cache) {
      totalSize += cached.blob.size;
    }
    
    // If over limit, clear oldest entries
    if (totalSize > this.options.maxCacheSize) {
      const entries = Array.from(this.cache.entries())
        .sort((a, b) => a[1].timestamp - b[1].timestamp);
      
      while (totalSize > this.options.maxCacheSize && entries.length > 0) {
        const [oldKey, oldCached] = entries.shift();
        totalSize -= oldCached.blob.size;
        this.cache.delete(oldKey);
      }
    }
    
    this.cache.set(key, {
      blob,
      timestamp: Date.now()
    });
  }
  
  /**
   * Get cache key for source
   */
  getSourceKey(source): any {
    return JSON.stringify({
      type: source.type,
      endpoint: source.endpoint,
      bucket: source.bucket,
      key: source.key,
      recordId: source.recordId,
      field: source.field
    });
  }
  
  /**
   * Clear cache
   */
  clearCache(): void {
    this.cache.clear();
    this.accessCache.clear();
  }
  
  /**
   * Delay helper
   */
  delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

export default FileSourceManager;
